// DirectAdjustCard — top-of-tab "Direct recharge / direct withdrawal"
// affordance for the admin Requests pages.
//
// Sits above the AdminMoneyPanel queue list inside the Recharge tab AND
// the Withdrawal tab on BOTH the player-requests hub and the
// agent-requests hub. Admin types a name / phone / email / UAID,
// picks the matched player or agent from a dropdown, and the existing
// DirectWalletAdjustModal opens pre-loaded with the right subject
// and the right direction (credit on Recharge tab, debit on
// Withdrawal tab).
//
// The point: not every credit / debit needs a player or agent to
// submit a request first. Sometimes admin collected cash in person,
// sometimes admin needs to claw a mistaken credit back, sometimes
// admin is settling out-of-band with an agent. This card is the
// official "without waiting for a request" path.
//
// ─── Why a separate card on each tab? ─────────────────────────────
// We considered a single bottom-of-page button that opens a picker
// modal. Two reasons we chose the inline card instead:
//
//   1. The picker IS the affordance. Putting the search input
//      directly into the tab makes the "you can do this" mental
//      model obvious. A floating button hides the capability and
//      requires admin to remember it.
//
//   2. Both Recharge AND Withdrawal want this same shape. Embedding
//      the card at the top of each tab means symmetry: every queue
//      tab has the same "search + act" header, then a queue. The
//      muscle-memory transfers from Players → Agents and from
//      Recharge → Withdrawal without re-training.
//
// ─── Search behaviour ────────────────────────────────────────────
//   * Debounced 250ms so each keystroke doesn't fan out.
//   * Hits the existing list endpoints which already support `search`
//     (server-side LIKE on username / email / phone):
//       players → GET /admin/users?search=...&limit=8
//       agents  → GET /admin/hierarchy/agents?search=...&limit=8
//   * Results dropdown shows avatar + identity + a money chip (wallet
//     for players, pool for agents) + status — same info the kebab-
//     card carries, just denser so 4-5 candidates fit before scrolling.
//   * Clicking a row opens DirectWalletAdjustModal with that subject.
//     Closing the modal returns to the Requests tab with the queue
//     refreshed via onAfterAdjust.

import { useCallback, useEffect, useState } from 'react';
import { levelName } from '../lib/levelNames';
import { motion, AnimatePresence } from 'motion/react';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import DirectWalletAdjustModal, {
  type DirectAdjustSubject,
} from './DirectWalletAdjustModal';

type Scope = 'players' | 'agents';
type Dir   = 'in' | 'out';

type PlayerHit = {
  id: number;
  username: string;
  email: string;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  profile_image_url: string | null;
  status: string;
  wallet_balance: number;
  agent_id: number | null;
  agent_username: string | null;
};

type AgentHit = {
  id: number;
  uaid: string | null;
  username: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  status: string;
  level: number;
  parent_username: string | null;
  /** Numeric id of the parent agent. Null when the agent is admin-direct
   *  (parent is admin, no parent agent row). Needed by the modal to
   *  fetch the parent's level + pool for the upline-context chip. */
  parent_agent_id: number | null;
  wallet_balance: number;
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function DirectAdjustCard({
  scope, direction, onAfterAdjust,
}: {
  scope: Scope;
  direction: Dir;
  /** Fired after the DirectWalletAdjustModal closes following a
   *  successful submission, so the host page can refresh queue +
   *  pending counts. */
  onAfterAdjust: () => void;
}) {
  const [query, setQuery]         = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults]     = useState<Array<PlayerHit | AgentHit>>([]);
  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState<string | null>(null);
  const [open, setOpen]           = useState(false);
  const [picked, setPicked]       = useState<DirectAdjustSubject | null>(null);

  // Theme: emerald accent for in (credit), rose for out (debit). The
  // hub-level title is already "Recharge" or "Withdrawal", and we
  // mirror that semantic colour here so the eye can tell which tab
  // it's in without reading the title.
  const isCredit = direction === 'in';
  const accent = isCredit
    ? {
        pill:   'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30',
        icon:   'arrow_upward',
        iconCl: 'text-emerald-700 dark:text-emerald-300',
        ring:   'focus:ring-emerald-400/30',
      }
    : {
        pill:   'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200/60 dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/30',
        icon:   'arrow_downward',
        iconCl: 'text-rose-700 dark:text-rose-300',
        ring:   'focus:ring-rose-400/30',
      };

  // Debounce.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // 2026-05-16: empty query is now a valid "browse all" call. Admins
  // who forget a name (or the exact spelling of a phone) can just tap
  // the search box and scroll through every player / agent without
  // having to type. With `search=` blank the existing list endpoints
  // return the most recent rows (ORDER BY created_at DESC) so the
  // top of the list is the freshest entity, which matches admin
  // muscle memory ("the player I'm looking for probably onboarded
  // recently").
  //
  // Limit bumped from 8 → 15 so the dropdown has enough body to
  // scroll meaningfully on phones (max-h-72 below shows ~5 rows then
  // scrolls). Pagination beyond the first 15 isn't supported here —
  // admin can refine via search input if their target isn't in the
  // initial slice. Keeping the API parity (same endpoint, same shape)
  // means we don't need a new "browse" path on the backend.
  const search = useCallback(async () => {
    setErr(null);
    setLoading(true);
    try {
      const qs = debounced ? `search=${encodeURIComponent(debounced)}&` : '';
      if (scope === 'players') {
        const r = await adminApi.get<{ rows: PlayerHit[] }>(`/admin/users?${qs}limit=15`);
        setResults(r.rows);
      } else {
        const r = await adminApi.get<{ rows: AgentHit[] }>(`/admin/hierarchy/agents?${qs}limit=15`);
        setResults(r.rows);
      }
    } catch (e) { setErr((e as ApiError).message || 'Search failed'); }
    finally { setLoading(false); }
  }, [debounced, scope]);

  useEffect(() => { void search(); }, [search]);
  // Open the dropdown whenever the user has typed; closing is now
  // driven exclusively by row-pick / blur / explicit clear. The
  // "open on focus" branch lives on the input's onFocus handler
  // below so an empty search still surfaces the full browse list.
  useEffect(() => { if (debounced.length > 0) setOpen(true); }, [debounced]);
  // Clear search state when the tab (direction) or scope changes —
  // each tab is a fresh intent, so the previous query + dropdown
  // shouldn't carry over visually. Without this, switching from
  // Recharge → Withdrawal leaves the prior input value sitting in
  // the search box with the dropdown closed (so admin sees text but
  // no results until they delete and retype).
  useEffect(() => {
    setQuery('');
    setDebounced('');
    setResults([]);
    setOpen(false);
    setErr(null);
  }, [direction, scope]);

  function pick(hit: PlayerHit | AgentHit) {
    if ('agent_id' in hit) {
      // Player hit — carry the avatar + first/last so the modal's
      // identity card can render a display name. Backend returns
      // first/last as separate fields so we collapse them here.
      const p = hit as PlayerHit;
      const displayName = (p.first_name || p.last_name)
        ? `${p.first_name || ''} ${p.last_name || ''}`.trim()
        : p.username;
      setPicked({
        kind: 'player',
        id: p.id,
        username: p.username,
        displayName,
        avatarUrl: p.profile_image_url,
        agentId: p.agent_id,
        agentUsername: p.agent_username,
        walletBalance: p.wallet_balance,
      });
    } else {
      const a = hit as AgentHit;
      setPicked({
        kind: 'agent',
        id: a.id,
        username: a.username,
        avatarUrl: a.avatar_url,
        level: a.level,
        uaid: a.uaid,
        parentUsername: a.parent_username,
        // Carry the parent id too so the modal can fetch the parent's
        // level + pool for the "PARENT" chip. Null for admin-direct
        // agents — no parent agent to show.
        parentAgentId: a.parent_agent_id ?? null,
        poolBalance: a.wallet_balance,
      });
    }
    setOpen(false);
  }

  const verb = isCredit ? 'recharge' : 'withdraw';
  const verbCap = isCredit ? 'Recharge' : 'Withdrawal';
  const placeholder = scope === 'players'
    ? `Search a player to ${verb} (name · phone · email)…`
    : `Search an agent to ${verb} (name · phone · email · UAID)…`;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl bg-white dark:bg-arena-700/40
                   ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40
                   shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                   overflow-hidden"
      >
        {/* Header row */}
        <div className="px-3 sm:px-4 py-3 flex items-center gap-2 border-b border-arena-200/40 dark:border-arena-700/40">
          <span className={`grid place-items-center w-8 h-8 rounded-xl shrink-0 ${accent.pill}`}>
            <Icon name={accent.icon} size={16} filled />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] font-bold text-neutral-900 dark:text-white leading-tight">
              Direct {verbCap.toLowerCase()}
            </div>
            <div className="text-[10.5px] text-arena-600 dark:text-neon-200/65 leading-tight mt-0.5">
              {scope === 'players'
                ? `Pick a player and ${verb} without waiting for a request.`
                : `Pick an agent and ${verb} their pool without a request.`}
            </div>
          </div>
        </div>

        {/* Search input + results */}
        <div className="p-3 sm:p-4">
          <div className="relative">
            <Icon name="search" size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-arena-600 dark:text-neon-200/70 pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              // 2026-05-16: open the dropdown AND re-fetch on every
              // focus. The re-fetch is the load-bearing piece: after a
              // pick → modal → close cycle, onDone wipes `results` to
              // [] but doesn't change `debounced` or `scope`, so the
              // `useEffect(() => search(), [search])` upstream sees an
              // identical dep list and skips. Without an explicit
              // search() call here, the second focus would open an
              // empty dropdown forever (until the user typed something
              // or the page was reloaded). Calling search() directly
              // makes the focus idempotent — every tap gives admin a
              // fresh list with latest balances.
              onFocus={() => { setOpen(true); void search(); }}
              placeholder={placeholder}
              className={`w-full rounded-full pl-9 pr-9 py-2 text-[13px]
                          bg-white dark:bg-arena-800/60
                          ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40
                          text-neutral-900 dark:text-white
                          placeholder-arena-400 dark:placeholder-white/40
                          outline-none focus:ring-2 ${accent.ring}`}
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full
                           hover:bg-arena-100/60 dark:hover:bg-arena-700/70"
                aria-label="Clear search"
              >
                <Icon name="close" size={14} className="text-arena-600 dark:text-neon-200/70" />
              </button>
            )}
          </div>

          {err && (
            <div className="mt-2 text-[11.5px] text-rose-700 dark:text-rose-300">{err}</div>
          )}

          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.12 }}
                className="mt-2 rounded-xl ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40
                           bg-white dark:bg-arena-800/85 backdrop-blur
                           max-h-72 overflow-y-auto"
              >
                {loading && (
                  <div className="px-3 py-3 text-[12px] text-arena-600 dark:text-neon-200/70">
                    <Icon name="progress_activity" size={12} className="animate-spin inline align-text-bottom mr-1" />
                    Searching…
                  </div>
                )}
                {!loading && results.length === 0 && (
                  <div className="px-3 py-3 text-[12px] text-arena-600 dark:text-neon-200/55 italic">
                    {/* When the user typed a query and got nothing, the
                        intent was "search" — "No matches" reads right.
                        When the field is empty and the list still came
                        back empty, the entity table is genuinely empty,
                        so a friendlier line ("No X to show") is more
                        accurate than "no matches" (since the user
                        wasn't searching anything specific). */}
                    {debounced
                      ? 'No matches.'
                      : (scope === 'players' ? 'No players to show.' : 'No agents to show.')}
                  </div>
                )}
                {!loading && results.map((hit) => (
                  <ResultRow key={`${scope}:${hit.id}`} scope={scope} hit={hit} onPick={() => pick(hit)} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Open the existing DirectWalletAdjustModal once admin picks
          a row. defaultDirection mirrors the tab so the modal opens
          ready-to-go (Recharge tab → credit; Withdrawal tab → debit). */}
      {picked && (
        <DirectWalletAdjustModal
          subject={picked}
          defaultDirection={isCredit ? 'credit' : 'debit'}
          onClose={() => setPicked(null)}
          onDone={() => {
            setPicked(null);
            setQuery('');
            setResults([]);
            onAfterAdjust();
          }}
        />
      )}
    </>
  );
}

// ─── Result row ────────────────────────────────────────────────────

function ResultRow({
  scope, hit, onPick,
}: {
  scope: Scope;
  hit: PlayerHit | AgentHit;
  onPick: () => void;
}) {
  if (scope === 'players') {
    const p = hit as PlayerHit;
    const displayName = (p.first_name || p.last_name)
      ? `${p.first_name || ''} ${p.last_name || ''}`.trim()
      : p.username;
    return (
      <button
        type="button"
        onClick={onPick}
        className="w-full px-3 py-2 flex items-center gap-2.5
                   hover:bg-arena-50 dark:hover:bg-arena-700/55 active:bg-arena-100/60 dark:active:bg-arena-700
                   text-left transition border-b border-arena-200/30 dark:border-arena-700/30 last:border-b-0"
      >
        <AdminAvatar url={p.profile_image_url} username={p.username} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-neutral-900 dark:text-white truncate">
            {displayName}
          </div>
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/65 truncate">
            {(((p.first_name || "") + " " + (p.last_name || "")).trim() || "—")} · #{p.id}
            {p.agent_username && (
              <span> · under @<span className="font-semibold text-arena-700 dark:text-neon-200/85">{p.agent_username}</span></span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[12px] font-display font-extrabold text-cyan-700 dark:text-cyan-300 tabular-nums leading-none">
            {inr(p.wallet_balance)}
          </div>
          <div className="text-[9.5px] text-arena-500 dark:text-neon-200/55 leading-none mt-0.5">
            wallet
          </div>
        </div>
      </button>
    );
  }
  const a = hit as AgentHit;
  return (
    <button
      type="button"
      onClick={onPick}
      className="w-full px-3 py-2 flex items-center gap-2.5
                 hover:bg-arena-50 dark:hover:bg-arena-700/55 active:bg-arena-100/60 dark:active:bg-arena-700
                 text-left transition border-b border-arena-200/30 dark:border-arena-700/30 last:border-b-0"
    >
      <AdminAvatar url={a.avatar_url} username={a.username} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-neutral-900 dark:text-white truncate flex items-center gap-1.5">
          @{a.username}
          <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-400/15 px-1 py-0.5 rounded">
            {levelName(a.level)}
          </span>
        </div>
        <div className="text-[10.5px] text-arena-600 dark:text-neon-200/65 truncate">
          {a.parent_username ? `under @${a.parent_username}` : 'admin direct'} · #{a.id}
          {a.uaid && <span className="ml-1 font-mono">· {a.uaid}</span>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`text-[12px] font-display font-extrabold tabular-nums leading-none
          ${a.wallet_balance >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600 dark:text-red-400'}`}>
          {inr(a.wallet_balance)}
        </div>
        <div className="text-[9.5px] text-arena-500 dark:text-neon-200/55 leading-none mt-0.5">
          pool
        </div>
      </div>
    </button>
  );
}
